<script setup>
import { reactive, ref, computed, nextTick, onMounted, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAlert } from 'dashboard/composables';
import { ExceptionWithMessage } from 'shared/helpers/CustomErrors';
import { debounce } from '@chatwoot/utils';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import camelcaseKeys from 'camelcase-keys';
import {
  createContactSearcher,
  fetchContactableInboxes,
  processContactableInboxes,
  mergeInboxDetails,
  isPhoneNumberInput,
  resolvePhoneContact,
  isEvolutionWhatsappInbox,
  validateWhatsappNumber,
} from 'dashboard/components-next/NewConversation/helpers/composeConversationHelper';

import Popover from 'dashboard/components-next/popover/Popover.vue';
import ComposeNewConversationForm from 'dashboard/components-next/NewConversation/components/ComposeNewConversationForm.vue';
import CreateNewContactDialog from 'dashboard/components-next/Contacts/ContactsForm/CreateNewContactDialog.vue';

const props = defineProps({
  contactId: {
    type: String,
    default: null,
  },
  align: {
    type: String,
    default: 'end',
  },
  initialInboxId: {
    type: [String, Number],
    default: null,
  },
  openOnMount: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close']);

const searchContacts = createContactSearcher();
const store = useStore();
const { t } = useI18n();

const { fetchSignatureFlagFromUISettings } = useUISettings();

const popoverRef = ref(null);
const contacts = ref([]);
const selectedContact = ref(null);
const targetInbox = ref(null);
const isCreatingContact = ref(false);
const isFetchingInboxes = ref(false);
const isValidatingWhatsappNumber = ref(false);
const inboxValidationRequestId = ref(0);
const isSearching = ref(false);
const createContactDialogRef = ref(null);
const newContactInitialData = ref({});
const hasOpenedAutomatically = ref(false);

const formState = reactive({
  message: '',
  subject: '',
  ccEmails: '',
  bccEmails: '',
  attachedFiles: [],
});

const clearFormState = () => {
  Object.assign(formState, {
    subject: '',
    ccEmails: '',
    bccEmails: '',
    attachedFiles: [],
  });
};

const contactById = useMapGetter('contacts/getContactById');
const contactsUiFlags = useMapGetter('contacts/getUIFlags');
const currentUser = useMapGetter('getCurrentUser');
const globalConfig = useMapGetter('globalConfig/get');
const uiFlags = useMapGetter('contactConversations/getUIFlags');
const messageSignature = useMapGetter('getMessageSignature');
const inboxesList = useMapGetter('inboxes/getInboxes');

const sendWithSignature = computed(() =>
  fetchSignatureFlagFromUISettings(targetInbox.value?.channelType)
);

const directUploadsEnabled = computed(
  () => globalConfig.value.directUploadsEnabled
);

const activeContact = computed(() => contactById.value(props.contactId));

const onContactSearch = debounce(
  async query => {
    isSearching.value = true;
    contacts.value = [];
    try {
      const results = await searchContacts(query);
      // null means the request was aborted (a newer search is in-flight),
      if (results === null) return;
      contacts.value = results;
      isSearching.value = false;
    } catch (error) {
      isSearching.value = false;
      useAlert(t('COMPOSE_NEW_CONVERSATION.CONTACT_SEARCH.ERROR_MESSAGE'));
    }
  },
  400,
  false
);

const resetContacts = () => {
  contacts.value = [];
};

const selectContact = async contact => {
  selectedContact.value = contact;
  contacts.value = [];
  if (contact?.id) {
    isFetchingInboxes.value = true;
    try {
      const contactableInboxes = await fetchContactableInboxes(contact.id);
      selectedContact.value.contactInboxes = mergeInboxDetails(
        contactableInboxes,
        inboxesList.value
      );
    } finally {
      isFetchingInboxes.value = false;
    }
  }
};

const handleSelectedContact = async ({ value, action, ...rest }) => {
  if (action === 'create') {
    const input = String(value || '').trim();
    if (isPhoneNumberInput(input)) {
      isCreatingContact.value = true;
      try {
        const { contact, contactableInboxes } =
          await resolvePhoneContact(input);
        selectedContact.value = {
          ...contact,
          contactInboxes: mergeInboxDetails(
            processContactableInboxes(contactableInboxes || []),
            inboxesList.value
          ),
        };
        contacts.value = [];
      } catch (error) {
        useAlert(
          error?.response?.data?.error ||
            t('COMPOSE_NEW_CONVERSATION.CONTACT_SEARCH.ERROR_MESSAGE')
        );
      } finally {
        isCreatingContact.value = false;
      }
      return;
    }

    newContactInitialData.value = input.startsWith('+')
      ? { phoneNumber: input }
      : { email: input };
    createContactDialogRef.value?.dialogRef.open();
    return;
  }

  await selectContact(rest);
};

const saveNewContact = async contact => {
  isCreatingContact.value = true;
  try {
    const createdContact = await store.dispatch('contacts/create', contact);
    createContactDialogRef.value?.onSuccess();
    await selectContact(camelcaseKeys(createdContact, { deep: true }));
    useAlert(
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.SUCCESS_MESSAGE')
    );
  } catch (error) {
    useAlert(
      error?.message || t('COMPOSE_NEW_CONVERSATION.FORM.ERROR_MESSAGE')
    );
  } finally {
    isCreatingContact.value = false;
  }
};

const clearSelectedContact = () => {
  inboxValidationRequestId.value += 1;
  isValidatingWhatsappNumber.value = false;
  selectedContact.value = null;
  targetInbox.value = null;
  clearFormState();
};

const handleTargetInbox = async inbox => {
  inboxValidationRequestId.value += 1;
  const requestId = inboxValidationRequestId.value;
  targetInbox.value = null;

  if (!inbox) {
    clearFormState();
    resetContacts();
    return;
  }

  if (isEvolutionWhatsappInbox(inbox) && selectedContact.value?.phoneNumber) {
    isValidatingWhatsappNumber.value = true;
    try {
      const result = await validateWhatsappNumber({
        phoneNumber: selectedContact.value.phoneNumber,
        inboxId: inbox.id,
        contactId: selectedContact.value.id,
      });
      if (requestId !== inboxValidationRequestId.value) return;
      if (!result.exists) {
        useAlert(t('COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_NUMBER_NOT_FOUND'));
        clearSelectedContact();
        return;
      }
    } catch (error) {
      if (requestId !== inboxValidationRequestId.value) return;
      useAlert(
        error?.response?.data?.error ||
          t('COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_VALIDATION_ERROR')
      );
      return;
    } finally {
      if (requestId === inboxValidationRequestId.value) {
        isValidatingWhatsappNumber.value = false;
      }
    }
  }

  if (requestId === inboxValidationRequestId.value) {
    targetInbox.value = inbox;
    resetContacts();
  }
};

const closeCompose = () => {
  popoverRef.value?.hide();
  if (!props.contactId) {
    // If contactId is passed as prop
    // Then don't allow to remove the selected contact
    selectedContact.value = null;
  }
  targetInbox.value = null;
  resetContacts();
};

const discardCompose = () => {
  clearFormState();
  formState.message = '';
  closeCompose();
};

const createConversation = async ({ payload, isFromWhatsApp }) => {
  try {
    const data = await store.dispatch('contactConversations/create', {
      params: payload,
      isFromWhatsApp,
    });
    const action = {
      type: 'link',
      to: `/app/accounts/${data.account_id}/conversations/${data.id}`,
      message: t('COMPOSE_NEW_CONVERSATION.FORM.GO_TO_CONVERSATION'),
    };
    discardCompose();
    useAlert(t('COMPOSE_NEW_CONVERSATION.FORM.SUCCESS_MESSAGE'), action);
    return true; // Return success
  } catch (error) {
    useAlert(
      error instanceof ExceptionWithMessage
        ? error.data
        : t('COMPOSE_NEW_CONVERSATION.FORM.ERROR_MESSAGE')
    );
    return false; // Return failure
  }
};

const onPopoverShow = () => {
  // Flag to prevent triggering drag n drop,
  // When compose modal is active
  emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, true);
};

const onPopoverHide = () => {
  emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, false);
  emit('close');
};

watch(
  activeContact,
  async (currentContact, previousContact) => {
    if (currentContact && props.contactId) {
      // Reset on contact change
      if (currentContact?.id !== previousContact?.id) {
        clearSelectedContact();
        clearFormState();
        formState.message = '';
      }

      // First process the contactable inboxes to get the right structure
      const processedInboxes = processContactableInboxes(
        currentContact.contactInboxes || []
      );
      // Then Merge processedInboxes with the inboxes list
      const contactInboxes = mergeInboxDetails(
        processedInboxes,
        inboxesList.value
      );
      selectedContact.value = {
        ...currentContact,
        contactInboxes,
      };
      if (props.initialInboxId) {
        targetInbox.value = contactInboxes.find(
          inbox => Number(inbox.id) === Number(props.initialInboxId)
        );
      }
      if (props.openOnMount && !hasOpenedAutomatically.value) {
        hasOpenedAutomatically.value = true;
        await nextTick();
        popoverRef.value?.show();
      }
    }
  },
  { immediate: true, deep: true }
);

onMounted(() => resetContacts());
</script>

<template>
  <Popover
    ref="popoverRef"
    :align="align"
    :show-content-border="false"
    @show="onPopoverShow"
    @hide="onPopoverHide"
  >
    <template #default="{ isOpen }">
      <slot name="trigger" :is-open="isOpen" />
    </template>
    <template #content>
      <ComposeNewConversationForm
        :form-state="formState"
        :contacts="contacts"
        :contact-id="contactId"
        :is-loading="isSearching"
        :current-user="currentUser"
        :selected-contact="selectedContact"
        :target-inbox="targetInbox"
        :is-creating-contact="isCreatingContact"
        :is-fetching-inboxes="isFetchingInboxes || isValidatingWhatsappNumber"
        :is-direct-uploads-enabled="directUploadsEnabled"
        :contact-conversations-ui-flags="uiFlags"
        :contacts-ui-flags="contactsUiFlags"
        :message-signature="messageSignature"
        :send-with-signature="sendWithSignature"
        @search-contacts="onContactSearch"
        @reset-contact-search="resetContacts"
        @update-selected-contact="handleSelectedContact"
        @update-target-inbox="handleTargetInbox"
        @clear-selected-contact="clearSelectedContact"
        @create-conversation="createConversation"
        @discard="discardCompose"
      />
    </template>
  </Popover>
  <CreateNewContactDialog
    ref="createContactDialogRef"
    :initial-data="newContactInitialData"
    @create="saveNewContact"
  />
</template>
